import { ref, reactive, computed, watch, nextTick } from 'vue'
import type { TaxConfig, CalculationLock } from '../types'
import { TaxCalculator } from '../utils/TaxCalculator'
import { useStorage } from './useStorage'

export function useTaxCalculator() {
  // Default tax configurations
  const defaultTaxes: TaxConfig[] = [
    { id: 'tax15', rate: 15, enabled: true, removable: false },
    { id: 'tax4', rate: 4, enabled: true, removable: false }
  ]

  // Load saved tax configuration
  const [storedTaxes, setStoredTaxes] = useStorage('taxConfig', defaultTaxes)
  
  // Reactive state
  const availableTaxes = ref<TaxConfig[]>(loadTaxConfiguration(storedTaxes.value))
  const newTaxRate = ref('')
  
  // Calculation lock to prevent circular updates
  const calculationLock = ref<CalculationLock>({ locked: false, source: null })
  
  // Current focused field (for bidirectional updates)
  const focusedField = ref<string | null>(null)
  const lastEditedField = ref<string | null>(null)
  const hasUserTyped = ref(false)
  
  // Input values (what user sees/types)
  const inputValues = reactive<Record<string, string>>({
    total: '',
    subtotal: ''
  })

  // Previous calculated values (for Copy All fallback)
  const previousValues = reactive<Record<string, string>>({
    total: '',
    subtotal: ''
  })

  // Computed properties
  const enabledTaxes = computed(() => 
    availableTaxes.value.filter(tax => tax.enabled)
  )

  const calculator = computed(() => new TaxCalculator(enabledTaxes.value))

  // Initialize input values for enabled taxes
  const initializeTaxInputs = () => {
    enabledTaxes.value.forEach(tax => {
      if (!(tax.id in inputValues)) {
        inputValues[tax.id] = ''
      }
      if (!(tax.id in previousValues)) {
        previousValues[tax.id] = ''
      }
    })
    
    // Remove inputs for disabled taxes
    Object.keys(inputValues).forEach(key => {
      if (key !== 'total' && key !== 'subtotal') {
        if (!enabledTaxes.value.some(tax => tax.id === key)) {
          delete inputValues[key]
          delete previousValues[key]
        }
      }
    })
  }

  // Load tax configuration from storage
  function loadTaxConfiguration(savedConfig: TaxConfig[]): TaxConfig[] {
    const newTaxes = [...defaultTaxes]
    const savedMap = new Map(savedConfig.map(tax => [tax.id, tax]))
    
    // Update default taxes with saved enabled state
    newTaxes.forEach(tax => {
      const saved = savedMap.get(tax.id)
      if (saved) {
        tax.enabled = saved.enabled
      }
    })
    
    // Add custom taxes
    savedConfig.forEach(savedTax => {
      if (savedTax.removable && !newTaxes.some(t => t.id === savedTax.id)) {
        newTaxes.push(savedTax)
      }
    })
    
    return newTaxes
  }

  // Save tax configuration
  const saveTaxConfiguration = () => {
    const config = availableTaxes.value.filter(tax => 
      tax.id && typeof tax.rate === 'number' && 
      typeof tax.enabled === 'boolean' && 
      typeof tax.removable === 'boolean'
    )
    setStoredTaxes(config)
  }

  // Perform calculation with lock mechanism
  const performCalculation = async (source: string, value: string) => {
    if (calculationLock.value.locked && calculationLock.value.source !== source) {
      return
    }

    const numericValue = calculator.value.parseInputValue(value)
    
    if (numericValue === 0) {
      resetAllValues()
      return
    }

    calculationLock.value = { locked: true, source }
    
    try {
      const calculation = calculator.value.calculate(source, numericValue)
      const formatted = calculator.value.formatCalculation(calculation)
      
      // Update all fields except the source
      await nextTick()
      
      Object.entries(formatted).forEach(([key, formattedValue]) => {
        const numericValue = stripFormatting(formattedValue)
        
        if (key !== source) {
          // Extract numeric part for input display
          inputValues[key] = numericValue
        }
        
        // Always store in previousValues for Copy All
        previousValues[key] = numericValue
      })

    } finally {
      // Release lock after a frame
      await nextTick()
      setTimeout(() => {
        calculationLock.value = { locked: false, source: null }
      }, 16) // One frame delay
    }
  }

  // Reset all values
  const resetAllValues = () => {
    Object.keys(inputValues).forEach(key => {
      inputValues[key] = '0.00'
      previousValues[key] = '0.00'
    })
  }

  // Handle input changes with debouncing
  let inputTimeout: number | null = null
  const onInput = (source: string, value: string) => {
    lastEditedField.value = source
    hasUserTyped.value = true
    
    if (inputTimeout) {
      clearTimeout(inputTimeout)
    }
    
    inputTimeout = setTimeout(() => {
      performCalculation(source, value)
    }, 100) // Small delay for smooth typing
  }

  // Handle field focus
  const onFocus = (field: string) => {
    focusedField.value = field
    lastEditedField.value = field
    hasUserTyped.value = false
    
    // Store current value in previousValues before clearing
    if (inputValues[field]) {
      previousValues[field] = inputValues[field]
    }
    
    // Clear the focused field for new input
    inputValues[field] = ''
  }

  // Handle field blur - restore value if user didn't type anything
  const onBlur = (field: string) => {
    // If user didn't type anything and field is empty, restore previous value
    if (!hasUserTyped.value && !inputValues[field] && previousValues[field]) {
      inputValues[field] = previousValues[field]
    }
    
    // Clear focused field
    if (focusedField.value === field) {
      focusedField.value = null
    }
  }

  // Tax management functions
  const toggleTax = (taxId: string) => {
    const tax = availableTaxes.value.find(t => t.id === taxId)
    if (tax) {
      tax.enabled = !tax.enabled
      
      if (!tax.enabled && inputValues[taxId]) {
        delete inputValues[taxId]
      } else if (tax.enabled) {
        inputValues[taxId] = ''
      }
      
      syncTaxConfig()
    }
  }

  const addCustomTax = () => {
    const rate = parseFloat(newTaxRate.value)
    
    if (!newTaxRate.value || isNaN(rate) || rate <= 0 || rate > 100) {
      return { success: false, message: 'Ingrese un porcentaje válido (0-100)' }
    }

    if (availableTaxes.value.some(tax => tax.rate === rate)) {
      return { success: false, message: 'Ya existe un impuesto con ese porcentaje' }
    }

    const newTax: TaxConfig = {
      id: `tax_${Date.now()}`,
      rate,
      enabled: true,
      removable: true
    }

    availableTaxes.value.push(newTax)
    inputValues[newTax.id] = ''
    previousValues[newTax.id] = ''
    newTaxRate.value = ''
    
    syncTaxConfig()
    
    return { success: true, message: `Impuesto ${rate}% agregado` }
  }

  const removeTax = (taxId: string) => {
    const index = availableTaxes.value.findIndex(t => t.id === taxId)
    const tax = availableTaxes.value[index]
    
    if (index > -1 && tax?.removable) {
      delete inputValues[taxId]
      availableTaxes.value.splice(index, 1)
      syncTaxConfig()
      
      return { success: true, message: 'Impuesto eliminado' }
    }
    
    return { success: false, message: 'No se pudo eliminar el impuesto' }
  }

  // Generate copy text for all values
  const generateCopyAllText = (): string => {
    const lines: string[] = []
    
    // Helper function to get the best available value
    const getValue = (field: string): string => {
      return inputValues[field] || previousValues[field] || '0.00'
    }
    
    // Always include total and subtotal
    lines.push(`Total: L${getValue('total')}`)
    lines.push(`Subtotal: L${getValue('subtotal')}`)
    
    // Include all enabled taxes
    enabledTaxes.value.forEach(tax => {
      lines.push(`Impuesto ${tax.rate}%: L${getValue(tax.id)}`)
    })
    
    return lines.join('\n')
  }

  // Strip 'L' prefix and commas from a formatted value string
  const stripFormatting = (value: string): string => {
    return value.replace(/^L/, '').replace(/,/g, '')
  }

  // Persist tax config changes and recalculate if the user has typed values
  const syncTaxConfig = () => {
    saveTaxConfiguration()
    initializeTaxInputs()
    if (lastEditedField.value && inputValues[lastEditedField.value]) {
      performCalculation(lastEditedField.value, inputValues[lastEditedField.value])
    }
  }

  // Watch for changes in available taxes
  watch(availableTaxes, () => {
    initializeTaxInputs()
  }, { deep: true })

  // Initialize on setup
  initializeTaxInputs()
  resetAllValues()

  return {
    // State
    availableTaxes,
    enabledTaxes,
    inputValues,
    previousValues,
    newTaxRate,
    focusedField,
    lastEditedField,

    // Actions
    onInput,
    onFocus,
    onBlur,
    toggleTax,
    addCustomTax,
    removeTax,
    resetAllValues,
    generateCopyAllText,
  }
}